import React from 'react';

/**
 * Store link for a shipped app. The most credible thing on the site — real
 * software, publicly verifiable, one click away — so it is styled as the primary
 * action on its card and every other link there becomes secondary.
 *
 * Deliberately not the official badge images: those carry their own visual
 * language and would fight the page. Inline SVG glyph at restrained size, drawn
 * from the platform marks, with explicit width/height so nothing reflows.
 */

type Store = 'play' | 'appstore';

const GLYPHS: Record<Store, React.ReactNode> = {
  // Play triangle
  play: (
    <path d="M3 1.6v16.8a.8.8 0 0 0 1.22.68l6.3-3.83L3 1.6Zm9.02 12.1 2.5-1.52-2.02-1.98-2.1 2.06 1.62.98v.46Zm.48-8.42L4.9.7 11.5 8.4l1-1.12ZM11.5 8.4 4.9.7l6.6 4.58L11.5 8.4Z" />
  ),
  // Apple mark
  appstore: (
    <path d="M13.62 10.63c-.02-2.1 1.72-3.11 1.8-3.16-.98-1.44-2.5-1.63-3.05-1.65-1.3-.13-2.54.76-3.2.76-.66 0-1.68-.74-2.76-.72-1.42.02-2.73.82-3.46 2.09-1.47 2.56-.38 6.35 1.06 8.43.7 1.02 1.54 2.16 2.63 2.12 1.06-.04 1.46-.68 2.74-.68 1.27 0 1.64.68 2.76.66 1.14-.02 1.86-1.03 2.56-2.05.8-1.18 1.13-2.32 1.15-2.38-.03-.01-2.21-.85-2.23-3.36ZM11.5 4.4c.58-.7.97-1.68.86-2.65-.83.03-1.84.55-2.44 1.25-.53.62-1 1.61-.87 2.56.93.07 1.87-.47 2.45-1.16Z" />
  ),
};

type StoreLinkProps = {
  store: Store;
  /** Exact wording, identical structure so the two read as a matched pair. */
  label: string;
  href: string;
  /** Names app and destination, e.g. "View Rezma on Google Play". */
  ariaLabel: string;
};

const StoreLink: React.FC<StoreLinkProps> = ({ store, label, href, ariaLabel }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className="ui flex min-h-[48px] w-full items-center justify-center gap-2.5 bg-lacquer px-5 text-[15px] font-semibold text-paper transition-colors duration-200 hover:bg-ink"
    style={{ borderRadius: 2 }}
  >
    <svg
      width="15"
      height="15"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      {GLYPHS[store]}
    </svg>
    {label}
    {/* external-link affordance */}
    <svg
      width="11"
      height="11"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0 opacity-70"
    >
      <path d="M4.5 1.5h6v6M10.5 1.5 4 8M9 7.5v3h-7.5V3h3" />
    </svg>
  </a>
);

export default StoreLink;
